import clsx from 'clsx'
import React, { FunctionComponent, HTMLAttributes } from 'react'

import { ColorTypes } from '../foundation/Colors/constants'
import { getColorClassName } from '../foundation/Colors/utils'
import { useDesignLanguagePerClass } from '../Theme'
import { HEADING_TAGS } from './constants'
import styles from './style.module.scss'
import { HeadingTags, TextLineClamp, TextSizes, TypoFontFace } from './types'

export interface HeadingProps extends HTMLAttributes<HTMLElement> {
  size?: TextSizes
  color?: ColorTypes
  bold?: boolean
  renderAs?: HeadingTags
  testNameSpace?: string
  lineClamp?: TextLineClamp
  fontFace?: TypoFontFace
}

export type HeadingComponent = FunctionComponent<HeadingProps>

const BaseHeading: HeadingComponent = ({ size, color, bold = true, renderAs, testNameSpace = '', children, lineClamp = 0, fontFace, ...otherProps }) => {
  const designLanguageStyles = useDesignLanguagePerClass(styles)

  if (typeof children === 'undefined') {
    return null
  }

  const HTMLTag = renderAs ?? HEADING_TAGS.H2

  const sizeClassName = typeof size !== 'undefined' && styles[`size${size}`]
  const colorClassName = typeof color !== 'undefined' && styles[getColorClassName(color)]
  const className = clsx(
    styles.text,
    styles.heading,
    sizeClassName,
    colorClassName,
    bold && styles.bold,
    lineClamp !== 0 && styles[`lineClamp${lineClamp}`],
    fontFace && styles[`fontFace${fontFace}`],
    designLanguageStyles.text,
    designLanguageStyles.heading
  )

  return (
    <HTMLTag className={className} {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-heading` })} {...otherProps}>
      {children}
    </HTMLTag>
  )
}

export const Heading: HeadingComponent = BaseHeading

export const H1: HeadingComponent = ({ children, renderAs, ...otherProps }) => (
  <Heading renderAs={HEADING_TAGS.H1} {...otherProps}>
    {children}
  </Heading>
)

export const H2: HeadingComponent = ({ children, renderAs, ...otherProps }) => <Heading {...otherProps}>{children}</Heading>

export const H3: HeadingComponent = ({ children, renderAs, ...otherProps }) => (
  <Heading renderAs={HEADING_TAGS.H3} {...otherProps}>
    {children}
  </Heading>
)

export const H4: HeadingComponent = ({ children, renderAs, ...otherProps }) => (
  <Heading renderAs={HEADING_TAGS.H4} {...otherProps}>
    {children}
  </Heading>
)

export const H5: HeadingComponent = ({ children, renderAs, ...otherProps }) => (
  <Heading renderAs={HEADING_TAGS.H5} {...otherProps}>
    {children}
  </Heading>
)

export const H6: HeadingComponent = ({ children, renderAs, ...otherProps }) => (
  <Heading renderAs={HEADING_TAGS.H6} {...otherProps}>
    {children}
  </Heading>
)
